import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { PropertyNotes } from "@/components/PropertyNotes";
import { NotebookPen } from "lucide-react";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Folder, FolderPlus, Upload, FileText, Download, Trash2, Eye, Loader2, FileArchive, Share2, Copy, Files, Mail } from "lucide-react";
import { ForwardEmailDialog } from "@/components/ForwardEmailDialog";

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function Documents() {
  const { user } = useAuth();
  const canNotes = can(user, ["admin", "regional", "accountant", "finance", "gm"]);
  const canManage = can(user, ["admin", "regional", "finance", "gm"]) || (user && user.role !== "owner" && user.role !== "employee");
  const canDeleteFolder = can(user, ["admin", "regional", "finance"]);

  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [folders, setFolders] = useState({ default: [], custom: [] });
  const [activeFolder, setActiveFolder] = useState("");
  const [docs, setDocs] = useState(null);
  const [selected, setSelected] = useState([]);

  const [upOpen, setUpOpen] = useState(false);
  const [upForm, setUpForm] = useState({ title: "", period: "" });
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [folderOpen, setFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState("");

  const [viewer, setViewer] = useState(null);
  const [share, setShare] = useState(null); // {url, expires_at, count}
  const [busy, setBusy] = useState(false);
  const [fwOpen, setFwOpen] = useState(false);
  const [fwDoc, setFwDoc] = useState(null);
  const [emailCfg, setEmailCfg] = useState({ email_configured: false, mail_from: "" });

  useEffect(() => { api.get("/ap-documents/config").then((r) => setEmailCfg(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    });
  }, []);

  const loadFolders = useCallback(() => {
    if (!propertyId) return;
    api.get("/document-folders", { params: { property_id: propertyId } }).then((r) => {
      setFolders(r.data);
      setActiveFolder((prev) => prev || r.data.default[0]);
    });
  }, [propertyId]);

  const loadDocs = useCallback(() => {
    if (!propertyId || !activeFolder) return;
    setDocs(null);
    setSelected([]);
    api.get("/documents", { params: { property_id: propertyId, folder: activeFolder } })
      .then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [propertyId, activeFolder]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const allFolders = [...folders.default, ...folders.custom.map((f) => f.name)];

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const doUpload = async () => {
    if (!upFile) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile);
      await api.post("/documents", fd, {
        params: { property_id: propertyId, folder: activeFolder, title: upForm.title, period: upForm.period },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setUpOpen(false);
      setUpForm({ title: "", period: "" });
      setUpFile(null);
      loadDocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const addFolder = async () => {
    try {
      await api.post("/document-folders", { property_id: propertyId, name: folderName });
      toast.success("Folder created");
      setFolderOpen(false);
      setFolderName("");
      loadFolders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"?`)) return;
    try {
      await api.delete(`/document-folders/${f.id}`);
      toast.success("Folder deleted");
      if (activeFolder === f.name) setActiveFolder(folders.default[0]);
      loadFolders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const viewFile = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setViewer({ url, type: res.data.type || d.file?.content_type || "", name: d.file?.filename || d.title });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadFile = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/file`, { params: { download: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file?.filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Document deleted");
      loadDocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadZip = async () => {
    setBusy(true);
    try {
      const res = await api.post("/documents/zip", { document_ids: selected }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "documents.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createShare = async () => {
    setBusy(true);
    try {
      const res = await api.post("/document-shares", { document_ids: selected, expires_in_days: 30 });
      // Build the link from the domain the app is actually served on, so it always
      // points at the app (not a mis-set FRONTEND_URL / wrong domain).
      const shareUrl = res.data.token ? `${window.location.origin}/share/${res.data.token}` : res.data.url;
      const shareData = { ...res.data, url: shareUrl };
      setShare(shareData);
      await navigator.clipboard?.writeText(shareUrl).catch(() => {});
      toast.success("Share link created — copied to clipboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Records</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Document Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">Store property reports, P&amp;L, STAR, tax &amp; insurance docs. Share bundles with banks or partners.</p>
        </div>
        <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setActiveFolder(""); }}>
          <SelectTrigger className="w-56" data-testid="doc-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
          <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        {/* Folders */}
        <div className="space-y-1" data-testid="folder-list">
          {canNotes && propertyId && (
            <button onClick={() => setActiveFolder("__notes__")} data-testid="folder-property-notes"
              className={`mb-2 flex w-full items-center gap-2 border border-dashed border-border px-3 py-2 text-left text-sm transition-colors ${activeFolder === "__notes__" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <NotebookPen className="h-4 w-4 shrink-0" /> Property Notes
            </button>
          )}
          {allFolders.map((name) => {
            const custom = folders.custom.find((f) => f.name === name);
            return (
              <div key={name} className="group flex items-center">
                <button
                  onClick={() => setActiveFolder(name)}
                  data-testid={`folder-${name}`}
                  className={`flex flex-1 items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${activeFolder === name ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
                >
                  <Folder className="h-4 w-4 shrink-0" /> <span className="truncate">{name}</span>
                </button>
                {custom && canDeleteFolder && (
                  <button onClick={() => removeFolder(custom)} className="ml-1 hidden p-1 text-destructive group-hover:block" data-testid={`del-folder-${name}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          {canManage && (
            <Button variant="ghost" size="sm" className="mt-2 w-full justify-start" onClick={() => setFolderOpen(true)} data-testid="add-folder-btn">
              <FolderPlus className="mr-2 h-4 w-4" /> New Folder
            </Button>
          )}
        </div>

        {activeFolder === "__notes__" && propertyId && (
          <PropertyNotes propertyId={propertyId} propertyName={props.find((p) => p.id === propertyId)?.name} showLink />
        )}
        {/* Documents */}
        <div className={`space-y-3 ${activeFolder === "__notes__" ? "hidden" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-heading text-lg font-bold">{activeFolder}</h2>
            <div className="flex items-center gap-2">
              {selected.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={downloadZip} disabled={busy} data-testid="download-zip-btn">
                    <FileArchive className="mr-2 h-4 w-4" /> ZIP ({selected.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={createShare} disabled={busy} data-testid="create-share-btn">
                    <Share2 className="mr-2 h-4 w-4" /> Share ({selected.length})
                  </Button>
                </>
              )}
              {canManage && (
                <Button size="sm" onClick={() => setUpOpen(true)} disabled={!propertyId || !activeFolder} data-testid="upload-doc-btn">
                  <Upload className="mr-2 h-4 w-4" /> Upload
                </Button>
              )}
            </div>
          </div>

          <Card className="rounded-none border">
            {docs === null ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : docs.length === 0 ? (
              <div className="p-12 text-center">
                <Files className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">No documents in this folder yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                      <TableCell>
                        <Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggleSel(d.id)} data-testid={`select-doc-${d.id}`} />
                      </TableCell>
                      <TableCell className="font-medium">{d.title}</TableCell>
                      <TableCell className="text-muted-foreground">{d.period || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtSize(d.file?.size)}</TableCell>
                      <TableCell className="text-muted-foreground">{d.uploaded_by}</TableCell>
                      <TableCell className="text-muted-foreground">{d.created_at ? fmtDate(d.created_at) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => viewFile(d)} data-testid={`view-doc-${d.id}`}><Eye className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => downloadFile(d)} data-testid={`download-doc-${d.id}`}><Download className="h-4 w-4" /></Button>
                          {canManage && (
                            <Button size="icon" variant="ghost" onClick={() => { setFwDoc(d); setFwOpen(true); }} title="Forward via email" data-testid={`forward-doc-${d.id}`}><Mail className="h-4 w-4" /></Button>
                          )}
                          {canManage && (
                            <Button size="icon" variant="ghost" onClick={() => deleteDoc(d)} data-testid={`delete-doc-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </div>

      {/* Upload dialog */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent data-testid="upload-doc-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>To “{activeFolder}”. PDF, Excel, Word, CSV or image (max 25MB).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={upForm.title} onChange={(e) => setUpForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g., June 2026 P&L" data-testid="doc-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Period (optional)</Label>
              <Input type="month" value={upForm.period} onChange={(e) => setUpForm((f) => ({ ...f, period: e.target.value }))} data-testid="doc-period" />
            </div>
            <div className="space-y-1.5">
              <Label>File</Label>
              <label className="flex cursor-pointer items-center gap-2 border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary">
                <Upload className="h-4 w-4" />
                {upFile ? upFile.name : "Choose file"}
                <input type="file" accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpOpen(false)}>Cancel</Button>
            <Button onClick={doUpload} disabled={uploading} data-testid="save-doc-btn">
              {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New folder dialog */}
      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent data-testid="new-folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Folder Name</Label>
            <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} data-testid="folder-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderOpen(false)}>Cancel</Button>
            <Button onClick={addFolder} disabled={!folderName.trim()} data-testid="save-folder-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share link dialog */}
      <Dialog open={!!share} onOpenChange={(o) => { if (!o) setShare(null); }}>
        <DialogContent data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Share Link Ready</DialogTitle>
            <DialogDescription>
              {share?.count} document(s) · anyone with this link can view &amp; download (no login). Expires {share?.expires_at ? fmtDate(share.expires_at) : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Input readOnly value={share?.url || ""} data-testid="share-link-input" />
            <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(share?.url || ""); toast.success("Copied"); }} data-testid="copy-share-link">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) { if (viewer) URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="doc-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><FileText className="h-5 w-5" /> {viewer?.name}</DialogTitle>
          </DialogHeader>
          {viewer && (viewer.type.includes("pdf") ? (
            <iframe title="doc" src={viewer.url} className="h-[70vh] w-full border border-border" />
          ) : viewer.type.includes("image") ? (
            <img src={viewer.url} alt="doc" className="max-h-[70vh] w-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
              <FileText className="h-10 w-10" />
              <p className="text-sm">Preview not available for this file type.</p>
              <a href={viewer.url} download={viewer.name}><Button variant="outline"><Download className="mr-2 h-4 w-4" /> Download</Button></a>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <ForwardEmailDialog open={fwOpen} onOpenChange={setFwOpen} apiBase="/documents" doc={fwDoc} emailCfg={emailCfg} onDone={loadDocs} />
    </div>
  );
}
